import type { BattlePhase, ImpactKind, ImpactTarget } from "../store/battleStore";
import {
  getEnemySpritePath,
  getPlayerSpritePath,
  type CharacterSpriteState
} from "../assets/characterSprites";

export interface BattleOpponentVisual {
  attackStyle: "melee" | "ranged" | "magic";
  isBoss: boolean;
  name: string;
  tier: number;
  variant: number;
}

interface BattleActorsProps {
  eventId: number;
  impactTarget: ImpactTarget;
  locked: boolean;
  phase: BattlePhase;
  impactKind: ImpactKind;
  opponent: BattleOpponentVisual;
}

type ActorState = "idle" | "attack" | "hit";

export function BattleActors({
  eventId,
  impactTarget,
  locked,
  phase,
  impactKind,
  opponent
}: BattleActorsProps) {
  const playerState = resolveActorState("player", impactTarget, locked);
  const botState = resolveActorState("bot", impactTarget, locked);

  return (
    <div className="battle-actors" aria-hidden="true">
      <ActorSprite
        actor="player"
        eventId={eventId}
        impactKind={impactKind}
        state={playerState}
        attackStyle={impactKind === "special" ? "magic" : "melee"}
        terminal={phase === "won" ? "winner" : phase === "lost" ? "defeated" : null}
        variant={0}
      />
      <ActorSprite
        actor="bot"
        eventId={eventId}
        impactKind={impactKind}
        state={botState}
        attackStyle={opponent.attackStyle}
        terminal={phase === "lost" ? "winner" : phase === "won" ? "defeated" : null}
        variant={opponent.variant}
      />
    </div>
  );
}

function resolveActorState(
  actor: ImpactTarget,
  impactTarget: ImpactTarget,
  locked: boolean
): ActorState {
  if (!locked) {
    return "idle";
  }

  return actor === impactTarget ? "hit" : "attack";
}

function ActorSprite({
  actor,
  eventId,
  impactKind,
  state,
  terminal,
  attackStyle,
  variant
}: {
  actor: ImpactTarget;
  eventId: number;
  impactKind: ImpactKind;
  state: ActorState;
  terminal: "winner" | "defeated" | null;
  attackStyle: "melee" | "ranged" | "magic";
  variant: number;
}) {
  const terminalClass = terminal ? ` actor-sprite--${terminal}` : "";
  const impactClass = state === "hit" && impactKind !== "normal"
    ? " actor-sprite--heavy-hit"
    : "";
  const attackClass = state === "attack"
    ? ` actor-sprite--attack-${attackStyle}${impactKind === "special" ? " actor-sprite--special" : ""}`
    : "";
  const spriteState: CharacterSpriteState = terminal === "winner"
    ? "victory"
    : terminal === "defeated"
      ? "defeated"
      : state === "hit"
        ? "hurt"
        : state;
  const activeSpritePath = actor === "player"
    ? getPlayerSpritePath(spriteState)
    : getEnemySpritePath(variant, spriteState);
  const idleSpritePath = actor === "player"
    ? getPlayerSpritePath("idle")
    : getEnemySpritePath(variant, "idle");
  const imageDirectionClass = `actor-sprite__image--${actor}`;
  const hasTransitionFrame = !terminal && state !== "idle";

  return (
    <div
      className={`actor-sprite actor-sprite--${actor} actor-sprite--${state}${terminalClass}${impactClass}${attackClass}`}
      data-actor={actor}
      data-actor-state={terminal ?? state}
      data-impact-kind={impactKind}
      key={`${actor}-${state}-${terminal ?? "active"}-${impactKind}-${eventId}`}
    >
      <span className="actor-sprite__shadow" />
      {hasTransitionFrame ? (
        <img
          className={`actor-sprite__image ${imageDirectionClass} actor-sprite__image--base`}
          src={idleSpritePath}
          alt=""
          draggable={false}
        />
      ) : null}
      <img
        className={`actor-sprite__image ${imageDirectionClass}${hasTransitionFrame ? " actor-sprite__image--action" : ""}`}
        src={activeSpritePath}
        alt=""
        draggable={false}
      />
      {state === "attack" && impactKind === "special" ? (
        <span className={`actor-special-aura actor-special-aura--${actor}`} />
      ) : null}
      {state === "hit" ? <span className="actor-hit-flash" /> : null}
      {state === "attack" && attackStyle !== "melee" ? (
        <span className={`actor-projectile actor-projectile--${
          actor === "player" ? "hero-magic" : attackStyle
        }`} />
      ) : null}
    </div>
  );
}
